// src/lib/api/automation/templates.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, sanitizeInput } from '../shared/validation'

/**
 * Get automation templates
 * Migrated from supabaseApi.js lines 52-72
 */
const _getAutomationTemplates = async () => {
  console.log('[automation] getAutomationTemplates called')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    console.warn('[automation] No company ID found')
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('automation_templates')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false })
    
  if (error) {
    console.error('[automation] Error fetching automation templates:', error)
    throw error
  }

  console.log('[automation] getAutomationTemplates success, found', data?.length || 0, 'templates')
  return createSuccessResponse(data || [])
}

/**
 * Upsert automation template
 * Migrated from supabaseApi.js lines 73-115
 */
const _upsertAutomationTemplate = async (template) => {
  console.log('[automation] upsertAutomationTemplate called with template:', template)
  
  validateRequired(template, 'Template')
  validateRequired(template.name, 'Template name')
  validateRequired(template.type, 'Template type')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  const payload = {
    company_id: companyId,
    name: sanitizeInput(template.name),
    type: template.type,
    subject: template.type === 'email' ? (template.subject ? sanitizeInput(template.subject) : null) : null,
    body: template.body ? sanitizeInput(template.body) : null,
    is_active: true
  }

  if (template.id) {
    const { data, error } = await supabase
      .from('automation_templates')
      .update(payload)
      .eq('id', template.id)
      .eq('company_id', companyId)
      .select('*')
      .single()

    if (error) {
      console.error('[automation] Error updating automation template:', error)
      throw error
    }
    
    console.log('[automation] upsertAutomationTemplate success, updated template:', data.id)
    return { data }
  } else {
    const { data, error } = await supabase
      .from('automation_templates')
      .insert([{ ...payload }])
      .select('*')
      .single()

    if (error) {
      console.error('[automation] Error creating automation template:', error)
      throw error
    }
    
    console.log('[automation] upsertAutomationTemplate success, created template:', data.id)
    return { data }
  }
}

/**
 * Delete automation template
 * Migrated from supabaseApi.js lines 116-137
 */
const _deleteAutomationTemplate = async (templateId) => {
  console.log('[automation] deleteAutomationTemplate called for ID:', templateId)
  
  validateRequired(templateId, 'Template ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  const { error } = await supabase
    .from('automation_templates')
    .delete()
    .eq('id', templateId)
    .eq('company_id', companyId)

  if (error) {
    console.error('[automation] Error deleting automation template:', error)
    throw error
  }

  console.log('[automation] deleteAutomationTemplate success for template:', templateId)
  return { success: true }
}

// Export functions with error handling
export const getAutomationTemplates = withErrorHandling(_getAutomationTemplates, 'get automation templates', 'automation_templates')
export const upsertAutomationTemplate = withErrorHandling(_upsertAutomationTemplate, 'upsert automation template', 'automation_template')
export const deleteAutomationTemplate = withErrorHandling(_deleteAutomationTemplate, 'delete automation template', 'automation_template')